import { Lightbulb, BarChart3, Mic, Lock, CheckCircle2, TrendingUp } from 'lucide-react'

// ── Tone visual styling (mirrors live-controls/PersonalContextColumn MeetingTone) ──
// Colors form a ramp from healthiest to most concerning: emerald (constructive) →
// Rithmo teal (neutral, the baseline-good state) → amber → orange → red. Neutral is
// teal, NOT gray, so a calm meeting reads as "working", not "missing data". Slate
// gray is reserved for the fallback below (a tone we don't have a color for yet).
const TONE_STYLE = {
  constructive:  { bar: 'bg-emerald-500', label: 'Constructive',  chip: 'bg-emerald-100 text-emerald-700', text: 'text-emerald-700' },
  neutral:       { bar: 'bg-accent-500',  label: 'Neutral',       chip: 'bg-accent-100 text-accent-700',   text: 'text-accent-700'  },
  negative:      { bar: 'bg-amber-400',   label: 'Negative',      chip: 'bg-amber-100 text-amber-700',     text: 'text-amber-700'   },
  contentious:   { bar: 'bg-orange-400',  label: 'Contentious',   chip: 'bg-orange-100 text-orange-700',   text: 'text-orange-700'  },
  argumentative: { bar: 'bg-red-400',     label: 'Argumentative', chip: 'bg-red-100 text-red-700',         text: 'text-red-700'     },
}

const CONCERNING_STATUSES = new Set(['negative', 'contentious', 'argumentative'])

// Any tone we haven't explicitly styled (a future/unknown status) shows in neutral
// slate under its own capitalized name, rather than masquerading as teal "Neutral".
function getToneStyle(status) {
  if (TONE_STYLE[status]) return TONE_STYLE[status]
  const label = typeof status === 'string' && status
    ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
    : 'Other'
  return { bar: 'bg-slate-300', label, chip: 'bg-slate-100 text-slate-600', text: 'text-slate-600' }
}

const METRIC_LABEL = {
  talkShare: 'Talk share',
  contributions: 'Contributions',
  attentiveness: 'Attentiveness',
}

// "25m 14s" / "44s" — an explicit duration, never "25:14" (which reads like a clock time).
function formatDuration(ms) {
  const totalSec = Math.max(0, Math.round((ms ?? 0) / 1000))
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  if (m === 0) return `${s}s`
  return s === 0 ? `${m}m` : `${m}m ${s}s`
}

function capitalize(str) {
  if (typeof str !== 'string' || !str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

function formatValue(metric) {
  if (!metric) return null
  return metric.unit === 'percent' ? `${metric.value}%` : `${metric.value}`
}

function PrivateBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
      <Lock className="h-3 w-3" />
      Private to you
    </span>
  )
}

function TrendChip({ trend, direction }) {
  if (!trend || trend === '0' || direction === 'flat') {
    return <span className="text-xs tabular-nums text-slate-400">&mdash;</span>
  }
  const isUp = direction === 'up'
  return (
    <span className={`text-xs font-medium tabular-nums ${isUp ? 'text-emerald-600' : 'text-rose-500'}`}>
      {isUp ? '▲' : '▼'} {trend}
    </span>
  )
}

function TipsCard({ tips }) {
  if (!tips?.length) return null
  return (
    <section className="rounded-2xl border border-primary-200/60 bg-primary-50/30 p-5 shadow-sm">
      <div className="flex items-center gap-2.5 mb-3.5">
        <Lightbulb className="h-5 w-5 text-primary-500" />
        <h4 className="text-base font-bold text-slate-900">Tips</h4>
        <div className="ml-auto"><PrivateBadge /></div>
      </div>
      <ul className="space-y-2">
        {tips.slice(0, 3).map((tip, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm leading-5 text-slate-700">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-400" />
            <span>{tip}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}

function CoachingList({ items, dotClass }) {
  return (
    <ul className="space-y-2.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm leading-6 text-slate-700">
          <span className={`mt-2 h-1.5 w-1.5 shrink-0 rounded-full ${dotClass}`} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  )
}

// Private per-person coaching: full "what went well / what to improve" review.
// Intentionally uncapped — the lists are as long as the substance warrants.
function CoachingCard({ coaching }) {
  const wentWell = Array.isArray(coaching?.wentWell) ? coaching.wentWell : []
  const toImprove = Array.isArray(coaching?.toImprove) ? coaching.toImprove : []
  const progress = Array.isArray(coaching?.progress) ? coaching.progress : []
  if (!wentWell.length && !toImprove.length && !progress.length) return null

  return (
    <section className="rounded-2xl border border-primary-200/60 bg-primary-50/30 p-5 shadow-sm">
      <div className="flex items-center gap-2.5 mb-4">
        <Lightbulb className="h-5 w-5 text-primary-500" />
        <h4 className="text-base font-bold text-slate-900">Your coaching</h4>
        <div className="ml-auto"><PrivateBadge /></div>
      </div>

      <div className="space-y-5">
        {progress.length > 0 && (
          <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-indigo-500" />
              <h5 className="text-sm font-semibold text-slate-900">Since last time</h5>
            </div>
            <CoachingList items={progress} dotClass="bg-indigo-400" />
          </div>
        )}

        {wentWell.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2.5">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <h5 className="text-sm font-semibold text-slate-900">What went well</h5>
            </div>
            <CoachingList items={wentWell} dotClass="bg-emerald-400" />
          </div>
        )}

        {toImprove.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2.5">
              <Lightbulb className="h-4 w-4 text-primary-500" />
              <h5 className="text-sm font-semibold text-slate-900">What to work on</h5>
            </div>
            <CoachingList items={toImprove} dotClass="bg-primary-400" />
          </div>
        )}
      </div>
    </section>
  )
}

// Vertically-oriented scorecard for the compact right rail: one row per category
// (Talk share, Contributions, Attentiveness — each hidden when it has no data),
// with Communication style rendered as a peer row rather than a footer afterthought.
function ScorecardCard({ scorecard, communicationStyle }) {
  const metrics = scorecard
    ? ['talkShare', 'contributions', 'attentiveness']
        .map((key) => ({ key, metric: scorecard[key] }))
        .filter((m) => m.metric != null)
    : []

  const hasCommStyle = !!communicationStyle?.style
  const engagement = communicationStyle?.engagement || null
  if (metrics.length === 0 && !hasCommStyle && !engagement) return null

  // Trends compare against the user's recent prior meetings, not this one in isolation —
  // surface the baseline so "▼ -5.1" isn't a mystery. Only meaningful once at least one
  // numeric metric is showing a trend.
  const priorMeetingCount = scorecard?.priorMeetingCount ?? 0
  const hasTrend = metrics.some(({ metric }) => metric.trend && metric.trend !== '0')
  const showBaseline = priorMeetingCount > 0 && hasTrend

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2.5 mb-3.5">
        <BarChart3 className="h-5 w-5 text-primary-500" />
        <h4 className="text-base font-bold text-slate-900">Your scorecard</h4>
        <div className="ml-auto"><PrivateBadge /></div>
      </div>

      <div className="space-y-2.5">
        {metrics.map(({ key, metric }) => (
          <div
            key={key}
            className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/60 px-3.5 py-2.5"
          >
            <span className="text-xs font-medium text-slate-500">{METRIC_LABEL[key]}</span>
            <span className="flex items-baseline gap-2">
              <span className="text-lg font-semibold tabular-nums text-slate-900">{formatValue(metric)}</span>
              <TrendChip trend={metric.trend} direction={metric.direction} />
            </span>
          </div>
        ))}

        {/* Communication style and engagement are distinct dimensions, each its own row. */}
        {hasCommStyle && (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/60 px-3.5 py-2.5">
            <span className="text-xs font-medium text-slate-500">Communication style</span>
            <span className="text-sm font-medium text-right text-slate-800">{communicationStyle.style}</span>
          </div>
        )}

        {engagement && (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/60 px-3.5 py-2.5">
            <span className="text-xs font-medium text-slate-500">Engagement</span>
            <span className="text-sm font-medium text-slate-800">{capitalize(engagement)}</span>
          </div>
        )}
      </div>

      {showBaseline && (
        <p className="mt-3 text-[11px] leading-4 text-slate-400">
          Trends compared to your last {priorMeetingCount} meeting{priorMeetingCount === 1 ? '' : 's'}.
        </p>
      )}
    </section>
  )
}

function ToneCard({ tone }) {
  if (!tone || !tone.breakdown?.length) return null
  const currentStatus = tone.currentStatus ?? null
  const currentStyle = currentStatus ? getToneStyle(currentStatus) : null
  const isConcerning = !!tone.redFlag || (currentStatus && CONCERNING_STATUSES.has(currentStatus))

  const sectionBorder = isConcerning
    ? 'border-amber-200/80 bg-amber-50/40'
    : 'border-primary-200/60 bg-primary-50/30'

  return (
    <section className={`rounded-2xl border ${sectionBorder} p-5 shadow-sm`}>
      <div className="flex items-center gap-2.5 mb-3.5">
        <Mic className={`h-5 w-5 ${isConcerning ? 'text-amber-600' : 'text-primary-500'}`} />
        <h4 className="text-base font-bold text-slate-900">Your Tone</h4>
        {currentStyle ? (
          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${currentStyle.chip}`}>
            {currentStyle.label}
          </span>
        ) : null}
        <div className="ml-auto"><PrivateBadge /></div>
      </div>

      <div className="space-y-2.5">
        {tone.breakdown.map((entry) => {
          const style = getToneStyle(entry.status)
          return (
            <div key={entry.status}>
              <div className="flex items-center justify-between mb-1.5">
                <span className={`text-sm font-medium ${style.text}`}>{style.label}</span>
                <span className="text-xs tabular-nums text-slate-400">
                  {entry.pct}% &middot; {formatDuration(entry.durationMs)}
                </span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full rounded-full ${style.bar}`}
                  style={{ width: `${entry.pct}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>
      <p className="mt-3 text-[11px] leading-4 text-slate-400">
        Share of your speaking time spent in each tone.
      </p>
    </section>
  )
}

/**
 * Recap tab personal coaching: viewer-private Tips, Your Tone, and Personal Scorecard.
 *
 * Layout: full-width Coaching/Tips on top, then a two-column row — the Meeting Summary
 * (passed in via `summarySlot`) on the left and a compact right rail holding Tone (top)
 * and the Scorecard (below). Each sub-card hides itself when it has no data; the whole
 * block renders nothing when there's no summary and none of the cards have anything to
 * show (e.g. before Layer 2 has processed the meeting).
 */
export default function RecapPersonalSection({ coaching: payload, summarySlot = null }) {
  const data = payload || {}
  // `data.coaching` is the new private per-person coaching; `tips` is the
  // legacy fallback for meetings analyzed before the dedicated coaching call.
  const { tips, coaching: coachingNotes, scorecard, communicationStyle, tone } = data

  const hasCoaching =
    !!coachingNotes &&
    ((coachingNotes.wentWell?.length ?? 0) +
      (coachingNotes.toImprove?.length ?? 0) +
      (coachingNotes.progress?.length ?? 0) >
      0)
  const hasTips = !hasCoaching && Array.isArray(tips) && tips.length > 0
  const hasScorecard =
    !!communicationStyle?.style ||
    !!communicationStyle?.engagement ||
    (scorecard && (scorecard.talkShare || scorecard.contributions || scorecard.attentiveness))
  const hasTone = !!tone && Array.isArray(tone.breakdown) && tone.breakdown.length > 0
  const hasRail = hasTone || hasScorecard

  if (!hasCoaching && !hasTips && !hasRail && !summarySlot) return null

  return (
    <div className="space-y-6">
      {hasCoaching && <CoachingCard coaching={coachingNotes} />}
      {hasTips && <TipsCard tips={tips} />}
      {(summarySlot || hasRail) && (
        // Meeting Summary fills the left; the private Tone + Scorecard sit in a fixed,
        // compact right rail (Tone on top, Scorecard below) top-aligned with the summary.
        // The left spacer keeps the rail pinned right even when there's no summary.
        // Stacks vertically on narrow screens.
        <div className="flex flex-col lg:flex-row gap-6 lg:items-start">
          <div className="flex-1 min-w-0">{summarySlot}</div>
          {hasRail && (
            <div className="w-full space-y-6 lg:w-[22rem] lg:shrink-0">
              {hasTone && <ToneCard tone={tone} />}
              {hasScorecard && (
                <ScorecardCard scorecard={scorecard} communicationStyle={communicationStyle} />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
